import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from '@fortawesome/vue-fontawesome'
import AcceptPoliciesCheckbox from './forms/AcceptPoliciesCheckbox'
import ActionButton from './forms/ActionButton'
import AdminBar from './AdminBar'
import Alert from './messages/Alert'
import AlertMessage from './messages/AlertMessage'
import Bandana from './messages/Bandana'
import CarbonAd from './ads/CarbonAd'
import CodeBlock from './code/CodeBlock'
import CodeBlockCaption from './code/CodeBlockCaption'
import CodeBlockTabSet from './code/CodeBlockTabSet'
import CodeHighlight from './syntax/CodeHighlight'
import CookieConsent from './messages/CookieConsent'
import ElementLoading from './loaders/ElementLoading'
import FlashMessage from './messages/FlashMessage.vue'
import Frameworks from './content/Frameworks'
import FontAwesomeAd from './ads/FontAwesomeAd'
import ForceUpdateOnMounted from './ForceUpdateOnMounted'
import IconsLoading from './loaders/IconsLoading'
import LogoFlag from './branding/LogoFlag'
import LogoFlagAnimated from './branding/LogoFlagAnimated'
import Media from './containers/Media'
import Message from './messages/Message'
import ModalAutoRenew from './modals/ModalAutoRenew'
import ModalCancelSubscription from './modals/ModalCancelSubscription'
import ModalFrame from './modals/ModalFrame'
import ModalKitsAndDomains from './modals/ModalKitsAndDomains'
import ModalV7Livestream from './modals/ModalV7Livestream'
import ModalUpsell from './modals/ModalUpsell'
import MonetizationAd from './ads/MonetizationAd'
import Nudge from './messages/Nudge'
import ObfuscatedText from './content/ObfuscatedText'
import OldVersionAlert from './messages/OldVersionAlert'
import Page from './containers/Page'
import PageFocused from './containers/PageFocused'
import PageFooter from './PageFooter'
import PageFooterSimple from './PageFooter/Simple.vue'
import PageHeader from './PageHeader'
import PageHeaderLimited from './PageHeader/Limited.vue'
import PageHeaderSimple from './PageHeader/Simple.vue'
import PageLoading from './loaders/PageLoading'
import PagePurchase from './containers/PagePurchase'
import PlanCard from './PlanCard'
import ProAlert from './messages/ProAlert'
import ProTag from './messages/ProTag'
import ProgressBar from './usage/ProgressBar'
import QuestionAndAnswer from './content/QuestionAndAnswer'
import QuestionMarkBlock from './funsies/QuestionMarkBlock'
import SampleSpinner from './loaders/SampleSpinner'
import SelectCustom from './forms/SelectCustom'
import Setup from './messages/Setup'
import StyleCardHorizontal from './StyleCard/StyleCardHorizontal.vue'
import StyleCardVertical from './StyleCard/StyleCardVertical.vue'
import StyleCardBrands from './StyleCard/StyleCardBrands'
import ProFeatures from './marketing/ProFeatures'
import SubscriptionFeatures from './marketing/SubscriptionFeatures'
import Tag from './content/Tag'
import TextField from './forms/TextField'
import UsageMessages from './usage/UsageMessages.vue'
import YoutubeEmbed from './content/YoutubeEmbed'

/**
 * A function that registers the global components if passing both a name string and a component
 * definition. See VueJS documentation here: {@link https://v2.vuejs.org/v2/guide/components-registration.html Registration} and
 * here: {@link https://v2.vuejs.org/v2/guide/instance.html Vue Instance}
 */
export default (vueInstance) => {
  /**
   * A function that accepts a name string, a component definition, and parameter object. The parameter object contains a
   * boolean value: withNameToo, which defaults to false. If it is set to true it will ALSO register the component with the
   * component's name property.
   *
   * @param {string} name - The name associated with the app component being registered
   * @param {Object} component - The app component
   * @param {Object} params - An optional object with the "withNameToo" flag set
   */
  const comp = (name, component, params = {}) => {
    const { withNameToo = false } = params

    vueInstance.component(name, component)

    if (withNameToo && component.name) {
      vueInstance.component(component.name, component)
    }
  }

  comp('font-awesome-icon', FontAwesomeIcon)
  comp('font-awesome-layers', FontAwesomeLayers)
  comp('font-awesome-layers-text', FontAwesomeLayersText)
  comp('wa-accept-policies-checkbox', AcceptPoliciesCheckbox)
  comp('wa-action-button', ActionButton)
  comp('wa-admin-bar', AdminBar)
  comp('wa-alert-message', AlertMessage, { withNameToo: true })
  comp('wa-alert', Alert)
  comp('wa-bandana', Bandana)
  comp('wa-carbon-ad', CarbonAd)
  comp('wa-code-block-caption', CodeBlockCaption, { withNameToo: true })
  comp('wa-code-block-tab-set', CodeBlockTabSet, { withNameToo: true })
  comp('wa-code-block', CodeBlock, { withNameToo: true })
  comp('wa-code-highlight', CodeHighlight)
  comp('wa-cookie-conset', CookieConsent)
  comp('wa-element-loading', ElementLoading)
  comp('wa-flash-message', FlashMessage)
  comp('wa-frameworks', Frameworks)
  comp('wa-fontawesome-ad', FontAwesomeAd)
  comp('wa-force-update-on-mounted', ForceUpdateOnMounted)
  comp('wa-icons-loading', IconsLoading)
  comp('wa-icons-loading', IconsLoading)
  comp('wa-logo-flag-animated', LogoFlagAnimated)
  comp('wa-logo-flag', LogoFlag)
  comp('wa-media', Media)
  comp('wa-message', Message)
  comp('wa-modal-auto-renew', ModalAutoRenew)
  comp('wa-modal-cancel-subscription', ModalCancelSubscription)
  comp('wa-modal-frame', ModalFrame)
  comp('wa-modal-kits-and-domains', ModalKitsAndDomains)
  comp('wa-modal-v7-livestream', ModalV7Livestream)
  comp('wa-modal-upsell', ModalUpsell)
  comp('wa-monetization-ad', MonetizationAd)
  comp('wa-nudge', Nudge)
  comp('wa-obfuscated-text', ObfuscatedText)
  comp('wa-old-version-alert', OldVersionAlert)
  comp('wa-page-focused', PageFocused)
  comp('wa-page-footer-simple', PageFooterSimple)
  comp('wa-page-footer', PageFooter)
  comp('wa-page-header-limited', PageHeaderLimited)
  comp('wa-page-header-simple', PageHeaderSimple)
  comp('wa-page-header', PageHeader)
  comp('wa-page-loading', PageLoading)
  comp('wa-page-purchase', PagePurchase)
  comp('wa-page', Page)
  comp('wa-plan-card', PlanCard)
  comp('wa-pro-alert', ProAlert, { withNameToo: true })
  comp('wa-pro-tag', ProTag)
  comp('wa-progress-bar', ProgressBar)
  comp('wa-question-and-answer', QuestionAndAnswer)
  comp('wa-question-mark-block', QuestionMarkBlock)
  comp('wa-sample-spinner', SampleSpinner)
  comp('wa-select-custom', SelectCustom)
  comp('wa-setup', Setup)
  comp('wa-style-card-horizontal', StyleCardHorizontal)
  comp('wa-style-card-vertical', StyleCardVertical)
  comp('wa-style-card-brands', StyleCardBrands)
  comp('wa-pro-features', ProFeatures)
  comp('wa-subscription-features', SubscriptionFeatures)
  comp('wa-tag', Tag)
  comp('wa-text-field', TextField)
  comp('wa-usage-messages', UsageMessages)
  comp('wa-youtube-embed', YoutubeEmbed)
}
